import React from 'react';
import clsx from 'clsx';
import GlassCard from '../brand/GlassCard';

/**
 * A titled form section — the same container BaseForm draws around each of its
 * titled sections, available on its own.
 *
 * Settings tabs are footerless: they live inside a tab body that already owns
 * the page chrome, so they cannot adopt BaseForm's full shell (header + fixed
 * `glass-bar` footer) without drawing a second page inside a page. They still
 * need BaseForm's *rhythm* — a glass card per group, a `gap-4` field grid, a
 * right-aligned action row — which is exactly what this and `FormActions` are.
 *
 * The grid class map is static for the same reason it is in BaseForm: Tailwind's
 * JIT scans source text, so an interpolated `grid-cols-${n}` never reaches the
 * stylesheet.
 */

export interface FormSectionProps {
  /** Already-translated section heading. Omit for an untitled group. */
  title?: React.ReactNode;
  /** Muted line under the title. */
  description?: string;
  /** Small muted glyph in the header row. */
  icon?: React.ReactNode;
  /** Right side of the header row. */
  actions?: React.ReactNode;
  /** Columns for the field grid. Default 1. */
  gridCols?: 1 | 2 | 3 | 4;
  /** Renders children as-is instead of wrapping them in the field grid. */
  noGrid?: boolean;
  /**
   * OPT-IN: the section fills the height it is given and its body is a flex
   * column the children fill, instead of a stack sized by its content.
   *
   * For a body that is a LIST with its own scroller (see `listSectionLayout`).
   * A field form must not use it — fields are sized by their content and
   * stretching them just leaves a gap. Implies `noGrid`: a grid of fields and a
   * single filling child are different bodies.
   */
  fill?: boolean;
  className?: string;
  children: React.ReactNode;
}

const GRID_COLS_CLASS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 md:grid-cols-2',
  3: 'grid-cols-1 md:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
};

const FormSection: React.FC<FormSectionProps> = ({
  title,
  description,
  icon,
  actions,
  gridCols = 1,
  noGrid = false,
  fill = false,
  className,
  children,
}) => {
  const fillColumn = 'flex min-h-0 min-w-0 flex-1 flex-col';

  let body: React.ReactNode;
  if (fill) {
    body = <div className={fillColumn}>{children}</div>;
  } else if (noGrid) {
    body = <div className="space-y-4">{children}</div>;
  } else {
    body = (
      <div className={clsx('grid gap-4', GRID_COLS_CLASS[gridCols] || GRID_COLS_CLASS[1])}>
        {children}
      </div>
    );
  }

  if (!title && !actions) {
    return (
      <div className={clsx(fill && 'flex h-full min-h-0 flex-col', className)}>{body}</div>
    );
  }

  return (
    <GlassCard
      icon={icon}
      title={title}
      subtitle={description}
      actions={actions}
      fill={fill}
      className={className}
    >
      {body}
    </GlassCard>
  );
};

export default FormSection;
